import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import MainLayout from "../../../components/layout/MainLayout";
import DischargeAdviceForm from "./DischargeAdviceForm";
import {
  fetchDischargeAdvice,
  updateDischargeAdvice,
  clearError,
} from "../../../store/slices/dischargeAdviceSlice";

const EditDischargeAdvice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentDischargeAdvice, isLoading, error } = useSelector(
    (state) => state.dischargeAdvice
  );

  const [formData, setFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // === Fetch on Mount ===
  useEffect(() => {
    dispatch(clearError());
    dispatch(fetchDischargeAdvice(id));
  }, [dispatch, id]);

  // === Map Backend → Frontend ===
  useEffect(() => {
    if (currentDischargeAdvice) {
      const formatDate = (date) => (date ? date.split("T")[0] : "");

      setFormData({
        // patient_id: currentDischargeAdvice.patient_id || null,
        // case_id: currentDischargeAdvice.case_id || null,
        patientId: patientId || currentDischargeAdvice.patient_id || null,
        caseId: caseId || currentDischargeAdvice.case_id || null,
        clientName: currentDischargeAdvice.client_name || "",
        assignedPRP: currentDischargeAdvice.assigned_prp || "",
        psychiatricDiagnosis: currentDischargeAdvice.psychiatric_diagnosis || "",
        primaryCaretaker: currentDischargeAdvice.primary_caretaker || "",
        admissionDate: formatDate(currentDischargeAdvice.admission_date),
        dischargeDate: formatDate(currentDischargeAdvice.discharge_date),
        // dischargeReason: currentDischargeAdvice.discharge_reason || "",
        // otherReason:
        //   currentDischargeAdvice.discharge_reason === "Other"
        //     ? currentDischargeAdvice.other_reason || ""
        //     : "",
        dischargeReason: currentDischargeAdvice.discharge_reason || "",
        documentName: currentDischargeAdvice.document_name || "Discharge Advice Form",
        effectiveDate: formatDate(currentDischargeAdvice.effective_date),
        approvedBy: currentDischargeAdvice.approved_by || "",
        approvedDate: formatDate(currentDischargeAdvice.approved_date),
        nextRevisionBy: currentDischargeAdvice.next_revision_by || "",
        nextRevisionDate: formatDate(currentDischargeAdvice.next_revision_date),
      });
    }
  }, [currentDischargeAdvice]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errors = {};
    if (!formData?.clientName) errors.clientName = "Client name is required.";
    if (!formData?.dischargeDate) errors.dischargeDate = "Discharge date is required.";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // === Payload Formatter ===
  const formatPayload = (data) => {
    const toNull = (val) => (val === "" ? null : val);

    return {
      // patient_id: toNull(data.patient_id),
      // case_id: toNull(data.case_id),
      patient_id: toNull(data.patientId),
      case_id: toNull(data.caseId),
      client_name: data.clientName,
      assigned_prp: toNull(data.assignedPRP),
      psychiatric_diagnosis: toNull(data.psychiatricDiagnosis),
      primary_caretaker: toNull(data.primaryCaretaker),
      admission_date: toNull(data.admissionDate),
      discharge_date: data.dischargeDate,
      // discharge_reason: data.dischargeReason,
      // other_reason: data.dischargeReason === "Other" ? toNull(data.otherReason) : null,
      discharge_reason: toNull(data.dischargeReason),
      document_name: data.documentName,
      effective_date: toNull(data.effectiveDate),
      approved_by: toNull(data.approvedBy),
      approved_date: toNull(data.approvedDate),
      next_revision_by: toNull(data.nextRevisionBy),
      next_revision_date: toNull(data.nextRevisionDate),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      Swal.fire({
        title: "Validation Error",
        html: Object.values(validationErrors).join("<br>"),
        icon: "warning",
      });
      return;
    }

    const payload = formatPayload(formData);

    try {
      // await dispatch(updateDischargeAdvice({ id, data: payload })).unwrap();
      await dispatch(updateDischargeAdvice({ case_id: caseId, data: payload })).unwrap();

      Swal.fire({
        title: "Updated!",
        text: "Discharge Advice updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // navigate("/phase07/discharge-advice");
      // Stay on edit page
    } catch (err) {
      const errorMsg =
        err?.errors
          ? Object.values(err.errors).flat().join("<br>")
          : err || "Failed to update discharge advice.";

      Swal.fire({
        title: "Error",
        html: errorMsg,
        icon: "error",
      });
    }
  };

  // === Loading State ===
  if (isLoading || !formData) {
    return (
      <MainLayout>
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading discharge advice...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // === Error State ===
  if (error) {
    return (
      <MainLayout>
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="card">
            <div className="card-body">
              <div className="alert alert-danger">
                <strong>Error:</strong> {error}
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
           <div className="d-flex justify-content-between align-items-center mb-3">
          <Breadcrumbs />
          <Link
            to={`/phase07/discharge-advice/print/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            <i className="ri-discharge-advice me-1"></i> Print
          </Link>
        </div>
        <div className="card">
          <div className="card-body">
            {/* Validation Errors */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="alert alert-warning">
                {Object.values(validationErrors).map((err, i) => (
                  <div key={i}>• {err}</div>
                ))}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <DischargeAdviceForm
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                isLoading={isLoading}
              />

              <div className="row mt-3">
                <div className="col-md-2 ms-auto">
                  <button
                    type="submit"
                    className="btn btn-primary w-100 sticky-form-submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-3-line me-1"></i> Update
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditDischargeAdvice;